'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import {
  formatUsdFromCents,
  queryTomVendingReconciliation,
  type TomVendingReconciliationRow,
} from '@/hooks/useTomVendingReports';
import { useNotification } from '@/contexts/NotificationContext';
import { useTomVendingLocationsList } from '@/hooks/useTomVendingLocation';
import { formatVisitDateLabel } from '../toms-vending/TomsVendingSiteVisitDetailContent';
import { downloadReconciliationXlsx, TOMS_REPORT_EXPORT_MAX_ROWS } from '@/utils/tomsVendingReportExport';
import { fetchAllReportRows } from '@/utils/downloadXlsx';

const PAGE_SIZE = 50;

function defaultDateRange() {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  };
}

type LocRow = { id: number; name?: string };

function varianceColor(cents: number): 'success' | 'warning' | 'error' | 'default' {
  const abs = Math.abs(cents);
  if (abs <= 200) return 'success';
  if (abs <= 1000) return 'warning';
  return 'error';
}

const TomsVendingReconciliationReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { data: locations = [], isLoading: locLoading } = useTomVendingLocationsList();

  const init = useMemo(() => defaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState(init.dateFrom);
  const [dateTo, setDateTo] = useState(init.dateTo);
  const [locationId, setLocationId] = useState<number | ''>('');
  const [data, setData] = useState<TomVendingReconciliationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await queryTomVendingReconciliation({
        dateFrom,
        dateTo,
        locationTomVendingId:
          locationId === '' || Number.isNaN(Number(locationId)) ? undefined : Number(locationId),
        skip: pageIndex * PAGE_SIZE,
        take: PAGE_SIZE,
      });
      setData(r.data);
      setTotal(r.total);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load reconciliation');
      setData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo, locationId, pageIndex, showError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleDownloadExcel = useCallback(async () => {
    setExporting(true);
    try {
      const locationTomVendingId =
        locationId === '' || Number.isNaN(Number(locationId)) ? undefined : Number(locationId);
      const { rows } = await fetchAllReportRows({
        pageSize: 200,
        maxRows: TOMS_REPORT_EXPORT_MAX_ROWS,
        fetchPage: async (skip, take) => {
          const r = await queryTomVendingReconciliation({
            dateFrom,
            dateTo,
            locationTomVendingId,
            skip,
            take,
          });
          return { rows: r.data, total: r.total };
        },
      });
      if (rows.length === 0) {
        showError('Nothing to export for the selected date range.');
        return;
      }
      downloadReconciliationXlsx(rows, dateFrom, dateTo);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to download Excel');
    } finally {
      setExporting(false);
    }
  }, [dateFrom, dateTo, locationId, showError]);

  const locOpts = locations as LocRow[];
  const selectedLoc = locationId === '' ? null : locOpts.find((l) => l.id === locationId) ?? null;

  const columns = useMemo<MRT_ColumnDef<TomVendingReconciliationRow>[]>(
    () => [
      {
        accessorKey: 'visitDate',
        header: 'Visit date',
        Cell: ({ cell }) => formatVisitDateLabel(String(cell.getValue() ?? '')),
      },
      { accessorKey: 'locationName', header: 'Location' },
      {
        id: 'machine',
        header: 'Machine',
        Cell: ({ row }) =>
          row.original.vendingMachineId != null
            ? row.original.assetId?.trim() || `#${row.original.vendingMachineId}`
            : 'Site total',
      },
      {
        accessorKey: 'expectedRetailCents',
        header: 'Expected retail',
        Cell: ({ cell }) => formatUsdFromCents(cell.getValue() as number),
      },
      {
        accessorKey: 'cashCollectedCents',
        header: 'Cash collected',
        Cell: ({ row }) =>
          row.original.missingCash ? (
            <Chip label="Not recorded" size="small" color="warning" variant="outlined" />
          ) : (
            formatUsdFromCents(row.original.cashCollectedCents)
          ),
      },
      {
        accessorKey: 'varianceCents',
        header: 'Variance',
        Cell: ({ row }) => (
          <Chip
            label={formatUsdFromCents(row.original.varianceCents)}
            size="small"
            color={varianceColor(row.original.varianceCents)}
          />
        ),
      },
      {
        accessorKey: 'variancePct',
        header: 'Var %',
        Cell: ({ cell }) => {
          const v = cell.getValue() as number | null;
          return v != null ? `${v.toFixed(1)}%` : '—';
        },
      },
      {
        id: 'flags',
        header: 'Flags',
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {row.original.missingPrices ? (
              <Chip label="Missing prices" size="small" variant="outlined" color="warning" />
            ) : null}
            {row.original.missingCash ? (
              <Chip label="No cash" size="small" variant="outlined" color="warning" />
            ) : null}
          </Box>
        ),
      },
    ],
    [],
  );

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Reports', href: '/app/toms-vending/reports' },
    { label: 'Cash reconciliation' },
  ];

  return (
    <Box>
      <TitleBar breadcrumbs={breadcrumbs} title="Cash reconciliation" />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
          Compare cash collected at each site visit to inferred retail from inventory counts. Expected
          retail uses sale prices effective at visit finalize time (price history when recorded). Large
          positive variance may indicate unpriced items, card sales, or counting gaps.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPageIndex(0);
            }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPageIndex(0);
            }}
          />
          <Autocomplete<LocRow>
            size="small"
            sx={{ minWidth: 260 }}
            options={locOpts}
            loading={locLoading}
            value={selectedLoc}
            onChange={(_, v) => {
              setLocationId(v?.id ?? '');
              setPageIndex(0);
            }}
            getOptionLabel={(l) => l.name?.trim() || `Location ${l.id}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Location (optional)" />}
          />
          <Button variant="contained" size="small" onClick={() => void fetchData()} disabled={isLoading}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void handleDownloadExcel()}
            disabled={isLoading || exporting || total === 0}
          >
            {exporting ? 'Downloading…' : 'Download Excel'}
          </Button>
          <Button component={RouterLink} to="/app/toms-vending/reports" variant="text" size="small" sx={{ textTransform: 'none' }}>
            All reports
          </Button>
        </Box>

        <MaterialReactTable
          columns={columns}
          data={data}
          manualPagination
          rowCount={total}
          enableGlobalFilter={false}
          onPaginationChange={(updater) => {
            setPageIndex(
              typeof updater === 'function'
                ? updater({ pageIndex, pageSize: PAGE_SIZE }).pageIndex
                : updater.pageIndex,
            );
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () =>
              navigate(
                `/app/toms-vending/site-visits/${row.original.siteVisitId}?locationId=${row.original.locationTomVendingId}`,
              ),
            sx: { cursor: 'pointer' },
          })}
          state={{ isLoading, pagination: { pageIndex, pageSize: PAGE_SIZE } }}
          initialState={{ density: 'compact' }}
        />
      </Box>
    </Box>
  );
};

export default TomsVendingReconciliationReportPage;
